import { useState } from 'react';
import { Plus, Search, Filter, RefreshCw, Loader2 } from 'lucide-react';
import { useSuppliers } from '../hooks';
import { useCurrentRoleSubjectPermission } from '../hooks/useCurrentRoleSubjectPermission';
import { SupplierCard, SupplierForm } from '../components';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import type { Supplier, SupplierCategory, CreateSupplierDTO } from '@yawi/types';

const CATEGORIES: SupplierCategory[] = [
  'PLOMERIA',
  'ELECTRICIDAD',
  'JARDINERIA',
  'LIMPIEZA',
  'MANTENIMIENTO',
  'SEGURIDAD',
  'FUMIGACION',
  'MUDANZAS',
  'REPARACIONES',
  'CERRAJERIA',
  'PINTURA',
  'CARPINTERIA',
  'AIRE_ACONDICIONADO',
  'ALBERCAS',
  'IMPERMEABILIZACION',
  'OTRO',
];

interface SuppliersPageProps {
  onSelectSupplier?: (supplier: Supplier) => void;
}

export const SuppliersPage = ({ onSelectSupplier }: SuppliersPageProps) => {
  const { t } = useI18n();
  const toast = useToast();
  const { canCreate: canCreateSupplier } = useCurrentRoleSubjectPermission('Supplier');

  const {
    suppliers,
    pagination,
    loading,
    error,
    params,
    search,
    filterByCategory,
    goToPage,
    createSupplier,
    refresh,
  } = useSuppliers();

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<unknown>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    search(searchTerm);
  };

  const handleCreateSupplier = async (data: CreateSupplierDTO) => {
    if (!canCreateSupplier) return;
    setFormLoading(true);
    setSubmissionError(null);
    try {
      await createSupplier(data);
      setShowForm(false);
      toast.success(t('suppliers.created'));
    } catch (err) {
      // Store the error so the form can show field-specific messages
      setSubmissionError(err);
      // Only show toast for non-validation errors (errors without field-specific messages)
      const errObj = err as Record<string, unknown>;
      if (!Array.isArray(errObj?.message)) {
        toast.error(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{t('suppliers.title')}</h1>
        {canCreateSupplier && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('suppliers.create')}
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('suppliers.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded-lg transition-colors ${
              showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </form>

        {/* Category Filters */}
        {showFilters && (
          <div className="p-4 bg-slate-50 rounded-lg">
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('suppliers.category.label')}</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => filterByCategory(undefined)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  !params.category ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                }`}
              >
                {t('common.all')}
              </button>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => filterByCategory(cat)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    params.category === cat ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {t(`suppliers.category.${cat.toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Suppliers Grid */}
      {!loading && (
        <div className="grid gap-4 md:grid-cols-2">
          {suppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onClick={() => onSelectSupplier?.(supplier)}
            />
          ))}

          {suppliers.length === 0 && (
            <div className="col-span-2 text-center py-12 text-slate-500">
              <p>{t('suppliers.noSuppliers')}</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={!pagination.hasPreviousPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.previous')}
          </button>
          <span className="px-4 py-1 text-sm text-slate-600">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={!pagination.hasNextPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && canCreateSupplier && (
        <SupplierForm
          onSubmit={handleCreateSupplier}
          onCancel={() => {
            setShowForm(false);
            setSubmissionError(null);
          }}
          loading={formLoading}
          submissionError={submissionError}
        />
      )}
    </div>
  );
};
